// CourseEventInfo.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseEventService } from '../../services/courseEventService';

// Иконка календаря
const CalendarIcon = ({ width = 12, height = 12 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Кеш в памяти с TTL (5 минут)
const CACHE_TTL = 5 * 60 * 1000;
const eventsCache = new Map();

const getCachedEvent = (courseId) => {
  const cached = eventsCache.get(courseId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedEvent = (courseId, data) => {
  eventsCache.set(courseId, {
    data,
    timestamp: Date.now()
  });
};

const CourseEventInfo = ({ courseId, className = '' }) => {
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const loadEvent = async () => {
      if (!courseId) {
        setLoading(false);
        return;
      }

      // Проверяем кеш
      const cached = getCachedEvent(courseId);
      if (cached !== null) {
        setEventInfo(cached);
        setLoading(false);
        return;
      }

      try {
        // Используем единый метод сервиса для получения доступных мероприятий
        const upcomingEvents = await courseEventService.getUpcomingEvents(courseId);
        
        if (!mounted) return;
        
        // Берем ближайшее мероприятие (первое в отсортированном списке)
        const upcomingEvent = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
        
        // Трансформируем в формат, ожидаемый компонентом
        let transformedEvent = null;
        if (upcomingEvent) {
          transformedEvent = {
            id: upcomingEvent.id,
            title: upcomingEvent.title,
            start_date: upcomingEvent.startDate,
            format: upcomingEvent.format,
            status: upcomingEvent.status,
            availableSlots: upcomingEvent.availableSlots,
            maxParticipants: upcomingEvent.maxParticipants,
            format_label: upcomingEvent.formatLabel,
            status_label: upcomingEvent.statusLabel
          };
        }
        
        // Сохраняем в кеш
        setCachedEvent(courseId, transformedEvent);
        setEventInfo(transformedEvent);
      } catch (error) {
        console.error(`Ошибка загрузки мероприятия для курса ${courseId}:`, error);
        setEventInfo(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadEvent();
  }, [courseId]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (eventInfo?.id) {
      navigate(`/training-events/${eventInfo.id}`);
    }
  }, [eventInfo?.id, navigate]);

  // Получение информации о доступности мест
  const getAvailabilityInfo = () => {
    if (!eventInfo || eventInfo.availableSlots === undefined) return null;
    
    if (eventInfo.availableSlots === 0) {
      return <span className="event-no-seats" title="Нет свободных мест">🔴</span>;
    }
    if (eventInfo.availableSlots <= 3) {
      return <span className="event-few-seats" title={`Осталось ${eventInfo.availableSlots} места`}>🟡</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`group-event-info loading ${className}`}>
        <div className="event-spinner-small"></div>
        <span>Поиск...</span>
      </div>
    );
  }

  if (!eventInfo) {
    return (
      <div className={`group-event-info no-event ${className}`}>
        <CalendarIcon width={12} height={12} />
        <span>Нет мероприятий</span>
      </div>
    );
  }

  const eventDate = new Date(eventInfo.start_date);
  const formattedDate = eventDate.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short'
  });

  const getFormatLabel = () => {
    switch(eventInfo.format) {
      case 'online': return 'Онлайн';
      case 'onsite': return 'Офлайн';
      case 'hybrid': return 'Гибрид';
      default: return eventInfo.format_label || '';
    }
  };

  const getStatusIcon = () => {
    switch(eventInfo.status) {
      case 'awaiting_confirmation': return '⏳';
      case 'confirmed': return '✓';
      default: return '';
    }
  };

  const availabilityInfo = getAvailabilityInfo();

  return (
    <div className={`group-event-info has-event ${className}`} onClick={handleClick}>
      <CalendarIcon width={10} height={10} />
      <span className="event-date">{formattedDate}</span>
      {eventInfo.format && (
        <span className={`event-format-mini format-${eventInfo.format}`}>
          {getFormatLabel()}
        </span>
      )}
      <span className="event-info-title-mini" title={eventInfo.title}>
        {eventInfo.title?.length > 25 ? `${eventInfo.title.substring(0, 25)}...` : eventInfo.title}
      </span>
      {availabilityInfo && (
        <span className="event-availability-info">{availabilityInfo}</span>
      )}
      {getStatusIcon() && (
        <span className="event-status-icon">{getStatusIcon()}</span>
      )}
    </div>
  );
};

export default React.memo(CourseEventInfo);